import React, { useState, useEffect, useRef, useMemo } from "react";
import "./CallsPage.scss";
import { useCallsViewModel } from "../viewmodel/useCallsViewModel";
import { useUnit } from "effector-react";
import {
  fetchDeclaredInterestCallsRequested,
  fetchBookmarkedCallsRequested,
  fetchArchivedCallsRequested,
  $callsStore,
} from "../store";
import { imageList } from "@/core/utils/imageList";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { FiltersDrawer } from "../components/FiltersDrawer";
import { SelectedFilters } from "../components/SelectedFilters";
import { MyCalls } from "../components/MyCalls";
import Loader from "@/core/components/loader/Loader";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { createRoute, ROUTES } from "@/core/constants/app";
import { slugify } from "@/core/utils/stringUtils";
import { formatRelativeTime } from "@/core/utils/format";
import { $userBasicInfo } from "@/features/user/store";
import { CommonModal } from "@/core/components/commonModal/CommonModal";
import { setModalIsOpened } from "@/core/store/app";
import { Tags } from "@/core/components/tags/Tags";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { Tooltip } from "@/core/components/tooltip/Tooltip";
import { Button } from "primereact/button";
import { useToast } from "@/core/hooks/useToast";
import { callsService } from "../service/api";
import Tracking from "@/core/utils/tracking";
import { SEO } from "@/core/components/SEO/SEO";

type CallsFilter = "all" | "for-you" | "my-calls";
type ViewMode = "grid" | "list";
type SortOption =
  | "created"
  | "most_popular_calls"
  | "deadline_desc"
  | "deadline_asc";

const sortOptions = {
  created: "Recently added",
  most_popular_calls: "Most popular",
  deadline_desc: "Deadline (latest first)",
  deadline_asc: "Deadline (earliest first)",
};

export const CallsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine initial filter based on route
  const getInitialFilter = (): CallsFilter => {
    if (location.pathname === ROUTES.CALLS_FOR_YOU) return "for-you";
    if (location.pathname === ROUTES.MY_CALLS) return "my-calls";
    return "all";
  };

  const [activeFilter, setActiveFilter] =
    useState<CallsFilter>(getInitialFilter());
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const [userBasicInfo] = useUnit([$userBasicInfo]);
  const { pro_user, is_new, partner, plan } = userBasicInfo || {};
  const [showProModal, setShowProModal] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortBy, setSortBy] = useState<SortOption>("created");
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(
    new Set(),
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    Tracking.pageView(
      "Calls",
      location.pathname,
      undefined,
      undefined,
      undefined,
      document.title,
      undefined,
      undefined,
    );
  }, []);

  // Update activeFilter when route changes
  useEffect(() => {
    const newFilter = getInitialFilter();
    if (newFilter !== activeFilter) {
      setActiveFilter(newFilter);
    }
  }, [location.pathname]);

  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: null as Date | null,
    toDate: null as Date | null,
    programmes: [],
    sectors: [],
    expenditures: [],
  });
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const loadMoreButtonRef = useRef<HTMLButtonElement>(null);

  const {
    loading,
    error,
    getFilteredCalls,
    getAllCallsCount,
    getForYouCallsCount,
    fetchCallsWithFilters,
    loadMoreCallsWithFilters,
    hasMoreCalls,
    loadMore,
    updateBookmark,
  } = useCallsViewModel();

  const [fetchDeclaredInterest, fetchBookmarked, fetchArchived, callsStore] =
    useUnit([
      fetchDeclaredInterestCallsRequested,
      fetchBookmarkedCallsRequested,
      fetchArchivedCallsRequested,
      $callsStore,
    ]);
  const toast = useToast();

  // Helper function to get days left and CSS class for badge
  const getDaysLeftInfo = (deadline: string) => {
    try {
      if (!deadline)
        return { daysLeft: 0, cssClass: "badgeYellow", text: "No deadline" };

      const now = new Date();
      const targetDate = new Date(deadline);

      if (isNaN(targetDate.getTime())) {
        return { daysLeft: 0, cssClass: "badgeYellow", text: "Invalid date" };
      }

      const diffTime = targetDate.getTime() - now.getTime();
      const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      const cssClass = daysLeft > 39 ? "badgeGreen" : "badgeYellow";
      const text = formatRelativeTime(deadline);

      return { daysLeft, cssClass, text };
    } catch (error) {
      return { daysLeft: 0, cssClass: "badgeYellow", text: "Error" };
    }
  };

  const getActiveFiltersCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.fromDate || appliedFilters.toDate) count++;
    count += appliedFilters.programmes.length;
    count += appliedFilters.sectors.length;
    count += appliedFilters.expenditures.length;
    return count;
  }, [appliedFilters]);

  const handleFilterChange = (filter: CallsFilter) => {
    setAppliedFilters({
      fromDate: null,
      toDate: null,
      programmes: [],
      sectors: [],
      expenditures: [],
    });
    // Navigate to the appropriate route based on filter
    if (filter === "all") {
      navigate(ROUTES.CALLS);
    } else if (filter === "for-you") {
      navigate(ROUTES.CALLS_FOR_YOU);
    } else if (filter === "my-calls") {
      navigate(ROUTES.MY_CALLS);
    }

    setActiveFilter(filter);

    // Handle different filter types
    if (filter === "for-you") {
      // Add type=Eligible filter for 'For you' tab
      fetchCallsWithFilters({
        sort: sortBy,
        search: searchQuery || undefined,
        type: "Eligible",
      });
    } else if (filter === "all") {
      // Remove type filter for 'All calls'
      fetchCallsWithFilters({
        sort: sortBy,
        search: searchQuery || undefined,
      });
    } else if (filter === "my-calls") {
      // Fetch all three endpoints for 'My calls'
      fetchDeclaredInterest(undefined);
      fetchBookmarked(undefined);
      fetchArchived(undefined);
    }
  };

  const toggleSectorExpansion = (callId: string) => {
    setExpandedSectors((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(callId)) {
        newSet.delete(callId);
      } else {
        newSet.add(callId);
      }
      return newSet;
    });
  };

  const handleSortChange = (newSort: SortOption) => {
    setSortBy(newSort);
    setIsSortDropdownOpen(false);
    const callFilters = convertFiltersToCallFilters(appliedFilters);
    callFilters.sort = newSort; // Override sort with the new value
    fetchCallsWithFilters(callFilters);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      fetchCallsWithFilters({
        sort: sortBy,
        search: searchQuery || undefined,
        type: activeFilter === "for-you" ? "Eligible" : "",
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleLoadMore = () => {
    const callFilters = convertFiltersToCallFilters(appliedFilters);
    loadMoreCallsWithFilters(callFilters);
  };

  const handleBookmarkAction = async (
    callId: string,
    isBookmarked: boolean,
  ) => {
    // The effect handles the API call, just trigger the event
    updateBookmark({ callId, isBookmarked: !isBookmarked });
  };

  const convertFiltersToCallFilters = (filters: typeof appliedFilters) => {
    const callFilters: any = {
      sort: sortBy,
      search: searchQuery || undefined,
      type: activeFilter === "for-you" ? "Eligible" : "",
    };

    // Add type filter for 'For you' tab
    if (activeFilter === "for-you") {
      callFilters.type = "Eligible";
    }

    // Add date filters - convert Date objects to ISO strings
    if (filters.fromDate) {
      callFilters.fromDate = filters.fromDate.toISOString().split("T")[0];
    }
    if (filters.toDate) {
      callFilters.toDate = filters.toDate.toISOString().split("T")[0];
    }

    // Add array filters (convert to comma-separated strings if needed)
    if (filters.programmes && filters.programmes.length > 0) {
      callFilters.programmes = filters.programmes.join(",");
    }
    if (filters.sectors && filters.sectors.length > 0) {
      callFilters.sectors = filters.sectors.join(",");
    }
    if (filters.expenditures && filters.expenditures.length > 0) {
      callFilters.expenditures = filters.expenditures.join(",");
    }

    return callFilters;
  };

  const handleFiltersApplied = (filters: any) => {
    filters.type = activeFilter === "for-you" ? "Eligible" : "";
    filters.type = setAppliedFilters(filters);
    // Filters are already fetched by the FiltersDrawer's handleApply
  };

  const handleRemoveFilter = (category: string, value: string) => {
    const newFilters = { ...appliedFilters };

    if (category === "fromDate" || category === "toDate") {
      (newFilters as any)[category] = null;
    } else {
      (newFilters as any)[category] = (appliedFilters as any)[category].filter(
        (item: string) => item !== value,
      );
    }

    setAppliedFilters(newFilters);

    // Trigger API call with updated filters

    const callFilters = convertFiltersToCallFilters(newFilters);
    callFilters.type = activeFilter === "for-you" ? "Eligible" : "";
    fetchCallsWithFilters(callFilters);
  };

  const handleClearAllFilters = () => {
    const clearedFilters = {
      fromDate: null as Date | null,
      toDate: null as Date | null,
      programmes: [],
      sectors: [],
      expenditures: [],
    };
    setAppliedFilters(clearedFilters);

    // Trigger API call with cleared filters
    const callFilters = convertFiltersToCallFilters(clearedFilters);
    callFilters.type = activeFilter === "for-you" ? "Eligible" : "";
    fetchCallsWithFilters(callFilters);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSortDropdownOpen(false);
      }
    };

    if (isSortDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSortDropdownOpen]);

  const filteredCalls = getFilteredCalls(activeFilter);

  // Get the current count based on filtered results
  const getCurrentResultsCount = () => {
    // For API-filtered results (for-you, all), use the pagination total_count
    if (activeFilter === "all") {
      return callsStore.pagination.total;
    }
    if (activeFilter === "for-you") {
      return callsStore.pagination.total;
    }
    // For client-side filtered results (my-calls), use filtered array length
    return filteredCalls.length;
  };

  const handleExportCall = async () => {
    setIsExporting(true);

    try {
      const exportBlob = await callsService.exportCalls();

      // Create a download link
      const url = window.URL.createObjectURL(exportBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `for-you-calls-export.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.showSuccess("Call exported successfully");
    } catch (error) {
      toast.showError("Failed to export call");
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="calls-page">
        <div className="pageTitle">Calls for proposals</div>
        <Loader overlay />
      </div>
    );
  }

  if (error) {
    return (
      <div className="calls-page">
        <div className="pageTitle">Calls for proposals</div>
        <div>Error loading calls: {error}</div>
      </div>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 67) return "#067647"; // green
    if (66 >= score && score >= 34) return "#f97316"; // orange
    return "#ef4444"; // red
  };

  return (
    <div className="calls-page">
      <SEO title={"Calls | EUcalls"} description={"Search for calls"} />
      <div className="pageTitle">Calls for proposals</div>
      <div className="calls-header">
        <div className="button-group">
          <button
            className={`button-group__item ${activeFilter === "all" ? "active" : ""}`}
            onClick={() => handleFilterChange("all")}
          >
            All calls ({callsStore.callGeneralData.total_calls})
          </button>

          <button
            className={`button-group__item ${activeFilter === "for-you" ? "active" : ""}`}
            onClick={() => {
              if (!userBasicInfo?.pro_user) {
                setShowProModal(true);
              } else {
                handleFilterChange("for-you");
              }
            }}
          >
            For you{" "}
            {userBasicInfo?.pro_user
              ? `(${callsStore.callGeneralData.total_calls_for_you})`
              : null}{" "}
            <span className="pro-badge">Pro</span>
          </button>

          <button
            className={`button-group__item ${activeFilter === "my-calls" ? "active" : ""}`}
            onClick={() => handleFilterChange("my-calls")}
          >
            My calls
          </button>
        </div>
      </div>
      {activeFilter !== "my-calls" && (
        <div className="call-filters">
          <div className="filter-search">
            <input
              type="text"
              placeholder="Search"
              className="search-input"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchSubmit}
            />
          </div>

          <div className="filter-controls">
            <button
              className={`filter-btn list-view-btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
            >
              <SvgIcon
                icon={viewMode === "grid" ? "icon-list" : "icon-grid-view"}
              />
              {viewMode === "grid" ? "List view" : "Grid view"}
            </button>

            <div className="sort-dropdown" ref={sortDropdownRef}>
              <button
                className="filter-btn sort-btn"
                onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
              >
                <SvgIcon icon="icon-sort" />
                {sortOptions[sortBy]}
                <SvgIcon
                  icon={
                    isSortDropdownOpen ? "icon-chevron-up" : "icon-chevron-down"
                  }
                />
              </button>

              {isSortDropdownOpen && (
                <div className="sort-dropdown-menu">
                  {Object.entries(sortOptions).map(([key, label]) => (
                    <button
                      key={key}
                      className={`sort-option ${sortBy === key ? "active" : ""}`}
                      onClick={() => handleSortChange(key as SortOption)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className="filter-btn filters-btn"
              onClick={() => setIsFiltersDrawerOpen(true)}
            >
              <SvgIcon icon="icon-filter" />
              Filters
              {getActiveFiltersCount > 0 && (
                <span className="filter-count">{getActiveFiltersCount}</span>
              )}
            </button>
            {activeFilter === "for-you" && (
              <div>
                {pro_user && (
                  <Button
                    className="exportButton"
                    onClick={() => {
                      if (pro_user) handleExportCall();
                    }}
                    disabled={isExporting}
                  >
                    <SvgIcon icon="icon-export" />
                    {isExporting ? "Exporting call" : "Export calls"}{" "}
                    <Tags type="pro" />
                    <Tooltip
                      text="Export all for-you calls as PDF document"
                      position="top"
                    >
                      <SvgIcon icon="icon-help" />
                    </Tooltip>
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeFilter !== "my-calls" && (
        <SelectedFilters
          filters={appliedFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
          type={activeFilter}
        />
      )}

      {activeFilter !== "my-calls" && (
        <div className="calls-results">{getCurrentResultsCount()} results</div>
      )}
      <div className="calls-content">
        {activeFilter === "my-calls" ? (
          <MyCalls />
        ) : (
          <>
            <div
              className={`calls-list ${viewMode === "list" ? "list-view" : "grid-view"}`}
            >
              {filteredCalls.map((call) => {
                const daysInfo = getDaysLeftInfo(call.deadline);
                return (
                  <div
                    key={`${slugify(call.title)}-${call.id}`}
                    className="call-item eu-card"
                  >
                    <Link to={createRoute(ROUTES.CALL_DETAIL, { id: call.id })}>
                      <div className="call-header">
                        <div className="call-type">
                          <div className="eu-flag">
                            <img src={call.programme.logo} alt="EU call" />
                          </div>
                          <span>{call.programme.name}</span>
                        </div>
                        <button
                          className={`bookmark-btn ${
                            call.is_call_bookmarked ||
                            call.is_call_declared_interest
                              ? "bookmarked"
                              : ""
                          } ${call.is_call_declared_interest ? "declared-interest" : ""}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            !call.is_call_declared_interest &&
                              handleBookmarkAction(
                                call.id,
                                call.is_call_bookmarked ||
                                  call.is_call_declared_interest,
                              );
                          }}
                          disabled={call.is_call_declared_interest}
                        >
                          <SvgIcon
                            icon={
                              call.is_call_declared_interest
                                ? "icon-declare"
                                : call.is_call_bookmarked
                                  ? "icon-bookmarked"
                                  : "icon-bookmark"
                            }
                            className={
                              call.is_call_declared_interest
                                ? "declare-icon"
                                : ""
                            }
                          />
                        </button>
                      </div>
                      {viewMode === "list" &&
                      typeof call.max_score === "string" ? (
                        <div className="call-footer">
                          {pro_user && (
                            <div
                              className="match-badge"
                              style={{
                                backgroundColor: getScoreColor(call.max_score),
                              }}
                            >
                              {call.max_score}% match
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>&nbsp;</div>
                      )}

                      <div className="call-content">
                        <div className="call-badges">
                          {call.is_new && (
                            <span className="badge new-badge">New</span>
                          )}
                          {daysInfo.daysLeft < 40 && (
                            <Tags
                              className={"badgeRed"}
                              type="custom"
                              text={"Expiring soon"}
                            />
                          )}
                          <span className="badge deadline-badge">
                            <SvgIcon icon="icon-clock" /> until{" "}
                            {new Date(call.deadline).toLocaleDateString(
                              "en-GB",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              },
                            )}
                          </span>
                          <div
                            className={`badge daysLeft ${daysInfo.cssClass}`}
                          >
                            <Tags
                              className={daysInfo.cssClass}
                              type="custom"
                              text={daysInfo.text}
                            />
                          </div>
                        </div>

                        <h3 className="call-title">{call.title}</h3>

                        <div className="call-budget">
                          € {parseInt(call.budget).toLocaleString()}
                        </div>

                        {viewMode === "list" ? (
                          <div className="call-meta">
                            <div className="interested-partners">
                              <span className="partners-label">
                                Interested Partners
                              </span>
                              {/* {!call?.interested_partners ||
                                (call?.interested_partners.length === 0 && "0")} */}
                              {!pro_user && (
                                <Tags
                                  type={"partner"}
                                  number={call?.interested_partners?.length}
                                />
                              )}
                              {pro_user &&
                                call?.interested_partners &&
                                call?.interested_partners?.length > 0 && (
                                  <div className="partners-avatars">
                                    {call?.interested_partners
                                      ?.slice(0, 4)
                                      .map((p, i) => (
                                        <div
                                          key={p.id || i}
                                          className="partner-avatar-wrapper"
                                          style={{
                                            transform:
                                              i > 0
                                                ? `translateX(-${10 * i}px)`
                                                : "none",
                                            zIndex: i > 0 ? 2 + (i - 1) : 1,
                                          }}
                                        >
                                          <div className="company-logo placeholder">
                                            <BgImage
                                              img={p.logo_uri}
                                              altImage={imageList.noOrgimage}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    {call.interested_partners &&
                                      call.interested_partners.length > 4 && (
                                        <span
                                          className="more-count-small"
                                          style={{
                                            transform: `translateX(-${12 * call.interested_partners.slice(0, 4).length}px)`,
                                            zIndex:
                                              2 +
                                              call.interested_partners.length,
                                          }}
                                        >
                                          +{call.interested_partners.length - 4}
                                        </span>
                                      )}
                                  </div>
                                )}
                              {pro_user &&
                                call?.interested_partners &&
                                call?.interested_partners?.length === 0 && (
                                  <div>
                                    <Tags
                                      type={"partner"}
                                      number={call?.interested_partners?.length}
                                      className="small"
                                    />
                                  </div>
                                )}
                            </div>
                            <div className="call-stats">
                              <div className="stat-item item-comments">
                                <SvgIcon icon="icon-message" />{" "}
                                {call.comments_count} comments
                              </div>
                              <div className="stat-item item-ideas">
                                <SvgIcon icon="icon-ideas" /> {call.ideas_count}{" "}
                                ideas
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="interested-partners">
                            <span className="partners-label">
                              Interested Partners
                            </span>
                            {/* {!call?.interested_partners ||
                              (call?.interested_partners.length === 0 && "0")} */}
                            {!pro_user && (
                              <Tags
                                type={"partner"}
                                number={call?.interested_partners?.length}
                              />
                            )}
                            {pro_user &&
                              call?.interested_partners &&
                              call?.interested_partners?.length > 0 && (
                                <div className="partners-avatars">
                                  {call?.interested_partners
                                    ?.slice(0, 4)
                                    .map((p, i) => (
                                      <div
                                        key={p.id || i}
                                        className="partner-avatar-wrapper"
                                        style={{
                                          transform:
                                            i > 0
                                              ? `translateX(-${10 * i}px)`
                                              : "none",
                                          zIndex: i > 0 ? 2 + (i - 1) : 1,
                                        }}
                                      >
                                        <div className="company-logo placeholder">
                                          <BgImage
                                            img={p.logo_uri}
                                            altImage={imageList.noOrgimage}
                                          />
                                        </div>
                                      </div>
                                    ))}

                                  {call.interested_partners &&
                                    call.interested_partners.length > 4 && (
                                      <span
                                        className="more-count-small"
                                        style={{
                                          transform: `translateX(-${12 * call.interested_partners.slice(0, 4).length}px)`,
                                          zIndex:
                                            2 + call.interested_partners.length,
                                        }}
                                      >
                                        +{call.interested_partners.length - 4}
                                      </span>
                                    )}
                                </div>
                              )}
                            {pro_user &&
                              call?.interested_partners &&
                              call?.interested_partners?.length === 0 && (
                                <div>
                                  <Tags
                                    type={"partner"}
                                    number={call?.interested_partners?.length}
                                    className="small"
                                  />
                                </div>
                              )}
                          </div>
                        )}

                        <div className="sectors-tags">
                          {expandedSectors.has(call.id) ? (
                            <>
                              {call.sectors.map((sector) => (
                                <span
                                  key={`${call.id}-${slugify(sector.name)}-${sector.id}`}
                                  className="sector-tag"
                                >
                                  {sector.name}
                                </span>
                              ))}
                              <span
                                className="sector-tag more-count"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleSectorExpansion(call.id);
                                }}
                              >
                                less
                              </span>
                            </>
                          ) : (
                            <>
                              {call.sectors.slice(0, 4).map((sector) => (
                                <span
                                  key={`${call.id}-${slugify(sector.name)}-${sector.id}`}
                                  className="sector-tag"
                                >
                                  {sector.name}
                                </span>
                              ))}
                              {call.sectors.length > 4 && (
                                <span
                                  className="sector-tag more-count"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleSectorExpansion(call.id);
                                  }}
                                >
                                  +{call.sectors.length - 4}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {viewMode === "grid" && (
                        <div className="call-footer">
                          {typeof call.max_score === "string" && pro_user ? (
                            <div
                              className="match-badge"
                              style={{
                                backgroundColor: getScoreColor(call.max_score),
                              }}
                            >
                              {call.max_score}% match
                            </div>
                          ) : (
                            <div>&nbsp;</div>
                          )}
                          <div className="call-stats">
                            <div className="stat-item item-comments">
                              <SvgIcon icon="icon-message" />{" "}
                              {call.comments_count} comments
                            </div>
                            <div className="stat-item item-ideas">
                              <SvgIcon icon="icon-ideas" /> {call.ideas_count}{" "}
                              ideas
                            </div>
                          </div>
                        </div>
                      )}
                    </Link>
                  </div>
                );
              })}
            </div>
            {hasMoreCalls() && (
              <div className="calls-action">
                {loadMore ? (
                  <Loader message="Loading" />
                ) : (
                  <button
                    ref={loadMoreButtonRef}
                    className="calls-loadMore"
                    onClick={handleLoadMore}
                    disabled={loadMore}
                  >
                    Load more
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <FiltersDrawer
        onApply={handleFiltersApplied}
        isOpen={isFiltersDrawerOpen}
        onClose={() => setIsFiltersDrawerOpen(false)}
        appliedFilters={appliedFilters}
        type={activeFilter}
      />
      <CommonModal
        onClose={() => {
          setShowProModal(false);
          setModalIsOpened(false);
        }}
        hasForm={false}
        width={430}
        height={470}
        icon={"icon-lighting"}
        isOpen={showProModal}
        actions={
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowProModal(false);
                setModalIsOpened(false);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              form="edit-idea-form"
              className="btn-primary"
              onClick={() => navigate("/settings/subscription")}
            >
              View plans
            </button>
          </div>
        }
      >
        <div className="proModal">
          <div className="proModal-title">
            Get full access to Pro features with a paid plan
          </div>
          <div className="proModal-description flex flex-col gap-1">
            You could do so much more with our Professional and Premium plans.
            Unlock features like:
            <div className="proModal-list">
              <div className="flex gap-05">
                <SvgIcon icon="icon-check" /> Personalized calls just for you
              </div>
              <div className="flex gap-05">
                <SvgIcon icon="icon-check" />
                Calls' interested partners
              </div>
              <div className="flex gap-05">
                <SvgIcon icon="icon-check" />
                Funding goals
              </div>
            </div>
          </div>
        </div>
      </CommonModal>
    </div>
  );
};
